import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { MapActionRequest, MapSelectionRequest, MapSelectionResult } from "../../compound-components/model";

/**
 * Service to handle map selection workflow communication between dialogs and map container
 */
@Injectable({
  providedIn: "root",
})
export class MapActionsService {
  // Separate BehaviorSubjects for each selection type
  private imageSelectionRequestSubject = new BehaviorSubject<MapSelectionRequest | null>(null);

  private geofencingSelectionRequestSubject = new BehaviorSubject<MapSelectionRequest | null>(null);

  private locationSelectionRequestSubject = new BehaviorSubject<MapSelectionRequest | null>(null);

  // Result streams
  private imageSelectionResultSubject = new BehaviorSubject<MapSelectionResult | null>(null);

  private geofencingSelectionResultSubject = new BehaviorSubject<MapSelectionResult | null>(null);

  private locationSelectionResultSubject = new BehaviorSubject<MapSelectionResult | null>(null);

  private mapActionRequestSubject = new Subject<MapActionRequest | null>();

  // Public observables for each selection type
  selectionImageRequest$ = this.imageSelectionRequestSubject.asObservable();

  selectionGeofencingRequest$ = this.geofencingSelectionRequestSubject.asObservable();

  selectionLocationRequest$ = this.locationSelectionRequestSubject.asObservable();

  // Result observables
  imageSelectionResult$ = this.imageSelectionResultSubject.asObservable();

  geofencingSelectionResult$ = this.geofencingSelectionResultSubject.asObservable();

  locationSelectionResult$ = this.locationSelectionResultSubject.asObservable();

  mapActionRequest$ = this.mapActionRequestSubject.asObservable();

  /**
   * Request map selection for image search
   */
  requestMapAction(request: MapActionRequest): void {
    this.mapActionRequestSubject.next(request);
  }

  /**
   * Request map selection for image search
   */
  requestImageSelection(request: MapSelectionRequest): void {
    this.imageSelectionRequestSubject.next(request);
  }

  /**
   * Request map selection for geofencing
   */
  requestGeofencingSelection(request: MapSelectionRequest): void {
    this.geofencingSelectionRequestSubject.next(request);
  }

  /**
   * Request map selection for location search
   */
  requestLocationSelection(request: MapSelectionRequest): void {
    this.locationSelectionRequestSubject.next(request);
  }

  /**
   * Send image selection result back to requesting component
   */
  sendImageSelectionResult(result: MapSelectionResult): void {
    this.imageSelectionResultSubject.next(result);
  }

  /**
   * Send geofencing selection result back to requesting component
   */
  sendGeofencingSelectionResult(result: MapSelectionResult): void {
    this.geofencingSelectionResultSubject.next(result);
  }

  /**
   * Send location selection result back to requesting component
   */
  sendLocationSelectionResult(result: MapSelectionResult): void {
    this.locationSelectionResultSubject.next(result);
  }

  /**
   * Clear all pending requests
   */
  clearAllRequests(): void {
    this.imageSelectionRequestSubject.next(null);
    this.geofencingSelectionRequestSubject.next(null);
    this.locationSelectionRequestSubject.next(null);
  }

  /**
   * Placeholder error handling function
   */
  showError(error: string): void {
    console.error("[MapSelectionService] Error:", error);
  }

  /**
   * Placeholder for location search functionality
   */
  searchByLocation(geometry: any): void {
    console.log("[MapSelectionService] searchByLocation called with geometry:", geometry);
  }
}